/* Post-mortem reports — read-only client for /api/reports.
   The bucket is private; the server hands out short-lived presigned links
   for attachments, so nothing here ever sees a bucket URL. */

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const authHeaders = (): HeadersInit => {
  const token = localStorage.getItem('authToken');
  return { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };
};

export interface ReportIndexEntry {
  date: string;
  title: string;
  costUsd: number | null;
  weekly: boolean;
  subtype: string | null;
  path: string;
}

export interface Heartbeat {
  at: string;
  lastReport: ReportIndexEntry | null;
  host?: string;
}

export interface ReportDoc {
  date: string;
  markdown: string;
  runResult: Record<string, unknown> | null;
}

export interface ReportFile { path: string; size: number; modified: string | null }

export class ReportsError extends Error {
  constructor(public status: number, message: string) { super(message); this.name = 'ReportsError'; }
}

async function get<T>(path: string): Promise<T> {
  const res = await fetch(`${API_BASE}/api/reports${path}`, { headers: authHeaders() });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new ReportsError(res.status, body.detail || body.message || `Request failed (${res.status})`);
  }
  return res.json();
}

export const reportsApi = {
  list: () => get<{ reports: ReportIndexEntry[]; heartbeat: Heartbeat | null }>(''),
  get: (date: string) => get<ReportDoc>(`/${date}`),
  files: (date: string) => get<{ files: ReportFile[] }>(`/${date}/files`),
  link: (date: string, path: string) => get<{ url: string }>(`/${date}/link?path=${encodeURIComponent(path)}`),
};

/* The agent ends every report with a fenced block titled "HANDOFF <date>";
   the operator pastes it into Claude Code to continue from where the agent
   stopped. Returns the block's inner text, or null. */
export function extractHandoff(markdown: string): string | null {
  const m = markdown.match(/```[^\n]*\n(HANDOFF[\s\S]*?)\n```/);
  return m ? m[1] : null;
}

/* Hours since the heartbeat; the page colors it by whether a trading day
   was likely missed (>30h on a weekday morning is the practical threshold). */
export function heartbeatAgeHours(hb: Heartbeat | null): number | null {
  if (!hb?.at) return null;
  const t = Date.parse(hb.at);
  return Number.isFinite(t) ? (Date.now() - t) / 36e5 : null;
}
